import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PillTabs } from "../components/lux";
import { PeriodPicker, periodRange } from "../components/PeriodPicker";
import { SalespeopleTab, salespeopleQuery } from "../components/sales/SalespeopleTab";
import { LeadsTab, VehicleDemandTab } from "../components/sales/LeadsTab";
import { PointsTab } from "../components/sales/PointsTab";

const TABS = [
  { key: "salespeople", label: "Salespeople" },
  { key: "leads", label: "Leads" },
  { key: "demand", label: "Vehicle Demand" },
  { key: "points", label: "Points" },
];

export default function SalesPerformance() {
  // SALES-028 follow-up: the selected tab lives in the URL (?tab=points), the
  // same way the calendar keeps its tab, so a link can open Points directly.
  // The default (Salespeople) is omitted from the URL, and an existing
  // ?person= link (no tab) still opens Salespeople with that rep's drawer.
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : "salespeople";
  const changeTab = (val) => {
    const next = new URLSearchParams(params);
    if (val === "salespeople") next.delete("tab");
    else next.set("tab", val);
    setParams(next, { replace: true });
  };
  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const range = periodRange(period, custom);

  // Money visibility comes from the reporting endpoint itself, so a login with
  // Sales Performance but no finance access never sees revenue. SALES-033: the
  // gate shares the Salespeople tab's exact query (same key), so one load of
  // that tab makes ONE request to /salespeople — the money check reads from it.
  const { data: gate } = useQuery(salespeopleQuery(range));
  const moneyOk = gate?.money_visible === true;

  return (
    <div className="space-y-7" data-testid="sales-performance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker value={period} onChange={setPeriod} custom={custom} onCustom={setCustom} testid="sp-period" />
        <span className="label-mono">{range.from || "start"} → {range.to || "today"}</span>
      </div>

      <PillTabs tabs={TABS} value={tab} onChange={changeTab} testid="sp-tabs" />

      {tab === "salespeople" && <SalespeopleTab range={range} moneyOk={moneyOk} initialPerson={params.get("person")} />}
      {tab === "leads" && <LeadsTab range={range} moneyOk={moneyOk} />}
      {tab === "demand" && <VehicleDemandTab range={range} />}
      {tab === "points" && <PointsTab range={range} />}
    </div>
  );
}
